import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, UserCheck, UserX, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { fmtDate, PRACTICE_STATUS } from "@/lib/academyFormat";
import { ACADEMY } from "@/constants/testIds";

const shift = (iso, days) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function PracticeRow({ row, today, onMark }) {
  const p = row.practice;
  const info = PRACTICE_STATUS[p.status] || PRACTICE_STATUS.scheduled;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3" data-testid={`${ACADEMY.agendaRowPrefix}${p.id}`}>
      <span className="w-14 text-sm font-medium tabular-nums">{p.time || "—"}</span>
      <div className="flex-1 min-w-[180px]">
        <Link to={`/academia/alumnos/${row.student.id}`} className="font-medium hover:underline">{row.student.name}</Link>
        <p className="text-xs text-muted-foreground">
          {row.course_name} · prácticas {row.practices_done} de {row.practices_required}
          {p.date !== today ? ` · agendada para el ${fmtDate(p.date)}` : ""}{p.note ? ` · ${p.note}` : ""}
        </p>
      </div>
      <span className={`text-xs rounded px-2 py-0.5 ${info.cls}`} data-testid={`${ACADEMY.agendaStatusPrefix}${p.id}`}>{info.label}</span>
      {p.status === "scheduled" ? (
        p.date <= today && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="text-[#5B7A54]" onClick={() => onMark(row, "attended")} data-testid={`${ACADEMY.agendaAttendedPrefix}${p.id}`}><UserCheck className="w-4 h-4" /> Asistió</Button>
            <Button size="sm" variant="outline" className="text-[#A34B49]" onClick={() => onMark(row, "absent")} data-testid={`${ACADEMY.agendaAbsentPrefix}${p.id}`}><UserX className="w-4 h-4" /> Faltó</Button>
          </div>
        )
      ) : (
        <Button size="icon" variant="ghost" title="Deshacer la marca" onClick={() => onMark(row, "scheduled")} data-testid={`${ACADEMY.agendaUndoPrefix}${p.id}`}><Undo2 className="w-4 h-4" /></Button>
      )}
    </div>
  );
}

// Fix D (2026-10-18): practicas del dia (carta, punto 17): recepcion, area
// academica e instructores marcan si el alumno asistio. Se agendan desde la
// ficha del alumno.
export default function AcademyPracticesPage() {
  const { toast } = useToast();
  const [day, setDay] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get("/academy/practices", { params: day ? { date: day } : {} });
      setData(res);
      if (!day) setDay(res.date);
    } catch (err) {
      setData({ items: [], late: [], today: day, date: day });
      toast({ title: "Error al cargar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  }, [day, toast]);

  useEffect(() => { load(); }, [load]);

  const mark = async (row, status) => {
    try {
      await api.put(`/academy/enrollments/${row.enrollment_id}/practices/${row.practice.id}`, { status });
      toast({ title: status === "attended" ? "Asistió" : status === "absent" ? "Marcado: faltó" : "Marca deshecha" });
      load();
    } catch (err) {
      toast({ title: "No se pudo", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const today = data?.today || day;
  return (
    <div className="space-y-6" data-testid={ACADEMY.agendaPage}>
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Prácticas</h1>
        <p className="text-base text-muted-foreground mt-1">Las prácticas agendadas de cada día. Se agendan desde la ficha del alumno.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="icon" onClick={() => setDay(shift(day || today, -1))} disabled={!day} title="Día anterior" data-testid={ACADEMY.agendaPrevButton}><ChevronLeft className="w-4 h-4" /></Button>
        <Input type="date" value={day} onChange={(ev) => ev.target.value && setDay(ev.target.value)} className="w-44" data-testid={ACADEMY.agendaDateInput} />
        <Button variant="outline" size="icon" onClick={() => setDay(shift(day || today, 1))} disabled={!day} title="Día siguiente" data-testid={ACADEMY.agendaNextButton}><ChevronRight className="w-4 h-4" /></Button>
        {day && today && day !== today && <Button variant="ghost" onClick={() => setDay(today)} data-testid={ACADEMY.agendaTodayButton}>Hoy</Button>}
      </div>

      {data && data.late.length > 0 && (
        <section className="rounded-xl border border-[#E8D9BC] bg-[#FBF7EF]" data-testid={ACADEMY.agendaLate}>
          <h2 className="px-4 pt-3 text-sm font-medium text-[#8A6A2F]">Sin marcar de días anteriores ({data.late.length})</h2>
          <div className="divide-y divide-[#EFE5D2]">
            {data.late.map((r) => <PracticeRow key={r.practice.id} row={r} today={today} onMark={mark} />)}
          </div>
        </section>
      )}

      <section className="rounded-xl border border-border bg-white" data-testid={ACADEMY.agendaList}>
        <h2 className="px-4 pt-3 text-sm font-medium text-muted-foreground">{day === today ? "Hoy" : fmtDate(day)}</h2>
        {!data ? <p className="px-4 py-6 text-sm text-muted-foreground">Cargando...</p> : data.items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground" data-testid={ACADEMY.agendaEmpty}>No hay prácticas agendadas para este día.</p>
        ) : (
          <div className="divide-y divide-border">
            {data.items.map((r) => <PracticeRow key={r.practice.id} row={r} today={today} onMark={mark} />)}
          </div>
        )}
      </section>
    </div>
  );
}
